import React from "react";
import Head from "next/head";
import Link from "next/link";
import Image from "next/image";

import { MdArrowOutward } from "react-icons/md";
import { GiArchiveResearch } from "react-icons/gi";
import { FaExclamationCircle } from "react-icons/fa";
import { BsFillLightbulbFill } from "react-icons/bs";
import { BiSupport } from "react-icons/bi";
import { IoMdChatbubbles } from "react-icons/io";
import { IoExtensionPuzzle } from "react-icons/io5";
import { AiOutlineStock } from "react-icons/ai";
import { IoLogoBuffer } from "react-icons/io";
import { IoNotifications } from "react-icons/io5";
import { ImEmbed2 } from "react-icons/im";
import { TbApi } from "react-icons/tb";
import { MdOutlineLan } from "react-icons/md";
import { Tb2Fa } from "react-icons/tb";
import { HiArrowLongRight } from "react-icons/hi2";

import { BASE_ASSETS_URL } from "@constants/constants";

import CtaOne from "@components/CTA/CtaOne";
import { CalComModalTrigger } from "@components/Modal/CalComModal";
import {
  CLMButtonPrimary1,
  CLMButtonPrimaryArrow,
} from "components/CLM/v3/Buttons";

const heroOptions = [
  {
    title: "Custom solutions",
    desciption: "We can build custom solutions for your business.",
    link: "#custom-solutions",
  },
  {
    title: "Privacy and Compliance",
    desciption: "Built to meet the highest security standards.",
    link: "#privacy-and-compliance",
  },
  {
    title: "Large Organization Scale",
    desciption: "We can scale BoloSign to meet the needs of your organization.",
    link: "#large-organization",
  },
];

const certifications = [
  {
    name: "ISO 27001:2022",
    image: "iso.png",
    description:
      "We follow ISO 27001 guidelines to establish, implement, maintain, and continually improve our information security management system (ISMS). ",
  },
  {
    name: "eIDAS",
    image: "eidas.png",
    description:
      "We comply with eIDAS by offering advanced electronic signatures that meet EU standards. Our platform ensures secure and legally binding transactions across all EU member states.",
  },
  {
    name: "CCPA",
    image: "ccpa.png",
    description:
      "We provide a transparent privacy policy that informs users about their data rights under the CCPA. Our platform allows users to access and delete their personal information easily. ",
  },
  {
    name: "ESIGN act",
    image: "esign.png",
    description:
      "We ensure every electronic signature on our platform is legally binding by verifying the signer's identity and capturing their intent to sign.",
  },
  {
    name: "GDPR",
    image: "gdpr.png",
    description:
      "BoloSign ensures lawful, fair, and transparent data processing by obtaining explicit consent from users before collecting their data.",
  },
  {
    name: "SOC 2 Type I",
    image: "soc1.png",
    description:
      "We undergo regular SOC 1 audits to demonstrate our commitment to secure and reliable financial reporting. Our internal controls are designed to protect client data and ensure the accuracy of financial transactions. ",
  },
  {
    name: "SOC 2 Type II",
    image: "soc2.png",
    description:
      "We adhere to SOC 2 criteria by implementing robust security measures, ensuring system availability, and maintaining data integrity. Our platform undergoes regular SOC 2 audits to verify that our controls are effective and meet industry standards. ",
  },
  {
    name: "UETA",
    image: "ueta.png",
    description:
      "BoloSign ensures clear consent from all parties to conduct business electronically, and our platform maintains robust records to validate each transaction.",
  },
  {
    name: "HIPAA",
    image: "hipaa2.png",
    description:
      "We implement stringent administrative, physical, and technical safeguards to protect electronic protected health information (ePHI) ensuring data confidentiality, integrity, and availability. ",
  },
];

const customization = [
  {
    title: "Company Logo",
    description:
      "Your logo will be seamlessly integrated into BoloSign, reinforcing to clients that BoloSign's functionality is a part of your software ecosystem.",
    icon: <IoLogoBuffer className="h-20 w-20 mx-auto text-dark" />,
  },
  {
    title: "Emails and Notifications",
    description:
      "All emails and notifications can be sent from your organization email address, with custom email content to meet your specific needs.",
    icon: <IoNotifications className="h-20 w-20 mx-auto text-dark" />,
  },
  {
    title: "Embedded Solutions",
    description:
      "The signature form and builder will be embedded into your website, flexibly adapted to match your brand identity. Colors, text, and other elements can be aligned with your branding.",
    icon: <ImEmbed2 className="h-20 w-20 mx-auto text-dark" />,
  },
];

const uniqueFeatures = [
  {
    title: "Signature API",
    description:
      "Utilize our flexible API to develop custom document signing solutions.",
    link: "/signature/api",
    icon: <TbApi className="h-12 w-12 text-dark" />,
  },
  {
    title: "Embedded Signing",
    description:
      "Easily integrate the signing form into your web or mobile apps.",
    link: "/signature/embedding",
    icon: <ImEmbed2 className="h-12 w-12 text-dark" />,
  },
  {
    title: "Multi-Tenant",
    description: "Create multiple accounts under one organization.",
    link: "https://help.boloforms.com/en/articles/15421407-how-to-create-a-workspace-inside-the-dashboard",
    icon: <MdOutlineLan className="h-12 w-12 text-dark" />,
  },
  {
    title: "2FA",
    description: "Secure your account with two-factor authentication.",
    link: "https://help.boloforms.com/en/articles/15429233-what-is-2-factor-authentication",
    icon: <Tb2Fa className="h-12 w-12 text-dark" />,
  },
];
const supportCards = [
  {
    title: "Tech Support",
    description:
      "We provide comprehensive support during the integration of BoloSign within your organization.",
    icon: BiSupport,
    theme: "light",
  },
  {
    title: "24/7 Communication",
    description: "You can count on our support at every step of the way.",
    icon: IoMdChatbubbles,
    theme: "dark",
  },
  {
    title: "Custom Features",
    description: "We can develop bespoke features that meet your unique needs.",
    icon: IoExtensionPuzzle,
    theme: "dark",
  },
  {
    title: "Organic Growth",
    description:
      "As your organization grows, BoloSign will adapt with you, ensuring the best customer experience.",
    icon: AiOutlineStock,
    theme: "light",
  },
];

const Enterprise = () => {
  return (
    <div className="space-y-12 sm:space-y-24 mb-10 max-w-screen-xl mx-auto md:mt-20 mt-8 md:px-8 px-4">
      <Head>
        <meta charSet="utf-8" />
        <title>BoloSign Enterprise</title>
        <meta
          name="description"
          content="Electronic signatures refer to signatures in digital format, distinguishing them from traditional handwritten signatures. These digital signatures hold full legal validity for indicating consent or endorsement of document contents. The prevalence of eSignatures is progressively supplanting manual signatures, serving as a versatile method to sign a wide array of documents spanning various sectors on a global scale."
        />
        <meta name="geo.region" content="IN" />
        <meta name="geo.position" content="22.351115;78.667743" />
        <meta name="ICBM" content="22.351115, 78.667743" />
        <meta name="author" content="Boloforms" />
        <meta name="publisher" content="BoloForms" />
        <meta
          name="keywords"
          content="eSignature, electronic signature, digital signature, eSign, eSignatures, electronic signatures, digital signatures, eSignature software, electronic signature software, digital signature software, eSignature solution, electronic signature solution, digital signature solution, eSignature app, electronic signature app, digital signature app, eSignature platform, electronic signature platform, digital signature platform, eSignature service, electronic signature service, digital signature service, eSignature tool, electronic signature tool, digital signature tool, eSignature online, electronic signature online, digital signature online, eSignature solution online, electronic signature solution online, digital signature solution online, eSignature software online, electronic signature software online, digital signature software online, eSignature platform online, electronic signature platform online, digital signature platform online"
        />
        <meta
          property="og:url"
          content="https://www.bolosign.com/signature/enterprise/"
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="BoloSign Enterprise" />
        <meta name="twitter:site" content="@boloforms" />
        <meta
          property="twitter:url"
          content="https://www.bolosign.com/signature/enterprise/"
        />
        <meta name="twitter:title" content="BoloSign Enterprise" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:creator" content="@boloforms" />
        <meta property="twitter:domain" content="https://bolosign.com" />
        <meta name="twitter:description" content="" />
        <meta property="og:description" content="" />
        <meta
          name="robots"
          content="INDEX, FOLLOW, MAX-IMAGE-PREVIEW:LARGE, MAX-SNIPPET:-1, MAX-VIDEO-PREVIEW:-1"
        />
        <link
          rel="canonical"
          href="https://www.bolosign.com/signature/enterprise/"
        />
      </Head>

      <section className="max-w-screen-lg mx-auto">
        <div className="text-center">
          <CtaOne
            heading1="BoloSign Enterprise"
            paragraphStyling="max-w-screen-md mx-auto"
            paragraph="Enhance your business with an enterprise-grade document signing solution. Custom integrations, advanced features, and dedicated support."
          />

          <div className="mt-2 w-full flex justify-center">
            <CalComModalTrigger calLink="boloforms/enterprise">
              <CLMButtonPrimary1
                buttonText={"Book free demo"}
                icon={<HiArrowLongRight />}
              />
            </CalComModalTrigger>
          </div>
        </div>

        <div className="mt-10 max-w-screen-xlg mx-auto flex items-stretch gap-8">
          <div className="flex flex-col md:flex-row gap-4 md:gap-8 max-w-5xl mx-auto">
            {heroOptions.map((option, index) => (
              <Link
                key={index}
                href={option.link}
                className="block relative flex-1 group rounded-lg border border-surface-dark bg-surface-light px-6 py-4 hover:bg-surface-dark transition-all duration-500 ease-in-out"
                scroll={false}
              >
                <p className="font-semibold text-lg mb-1">{option.title}</p>
                <p className="text-sm">{option.desciption}</p>

                <MdArrowOutward className="h-5 w-5 absolute top-2 right-2" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section id="custom-solutions">
        <div className="flex flex-col md:flex-row md:items-start gap-6">
          <div className="w-full md:w-1/2">
            <CtaOne
              heading2="BoloSign can be tailored to meet the specific needs of your organization"
              paragraph="With BoloSign Enterprise, you can gain access to precisely the solutions you require. We ensure BoloSign fits your business needs and develop unique solutions just for you."
            />

            <div className="space-y-6">
              <div className="flex items-start md:items-center gap-6">
                <div className="flex h-20 w-20 rounded-2xl border border-surface-dark bg-white p-4">
                  <FaExclamationCircle className="w-12 h-12 text-green-400" />
                </div>
                <div>
                  <p className="text-lg font-semibold">
                    Collecting requirements
                  </p>
                  <p className="mt-1">
                    We&apos;re here to help you identify the exact solutions
                    that will meet your needs.
                  </p>
                </div>
              </div>
              <div className="flex items-start md:items-center gap-6">
                <div className="flex h-20 w-20 rounded-2xl border border-surface-dark bg-white p-4">
                  <GiArchiveResearch className="w-12 h-12 text-blue-400" />
                </div>
                <div>
                  <p className="text-lg font-semibold">
                    Research and development
                  </p>
                  <p className="mt-1">
                    We help you to build a solution for your specific business
                    problem and integrate BoloSign with your existing systems.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full md:w-1/2">
            <Image
              width="532"
              height="419"
              alt="Payment Field"
              className="w-full rounded-xl"
              loading="lazy"
              src={`${BASE_ASSETS_URL}/v2/landing-page/only_solution1.png`}
              // src="https://www.docuseal.co/packs/static/images/pages/tools-db536ceab4c52f6ff0f7.svg"
            />
          </div>
        </div>
      </section>

      <section id="privacy-and-compliance">
        <div className="md:w-2/3">
          <CtaOne heading2="With the On-premises Enterprise the data remains within your organisation" />
        </div>

        <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
          {certifications.map((certification, index) => (
            <Link
              key={index}
              className="flex flex-col gap-2 p-8 rounded-2xl border border-surface-dark bg-surface-light hover:bg-surface-dark  transition-all duration-500 ease-in-out"
              href="https://security.boloforms.com/"
            >
              <Image
                src={`${BASE_ASSETS_URL}/v2/security/${certification.image}`}
                className="h-16 w-16 rounded-full"
                width={20}
                height={20}
                loading="lazy"
                alt={certification.name}
              />

              <div>
                <h3 className="text-xl font-semibold text-neutral-700">
                  {certification.name}
                </h3>
                <p className="mt-2 text-neutral-600">
                  {certification.description}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section id="large-organization">
        <div className="text-center max-w-screen-md mx-auto">
          <CtaOne
            heading1="Scalability for Large Organizations"
            paragraph="BoloSign Enterprise can easily scale to acccommodate the needs of large organizations. We offer customized solutions that cater to your specific requirements."
          />
        </div>

        <div className="grid   p-1 xl:grid-cols-2 gap-4">
          <div className="flex flex-col items-center justify-center gap-6 p-8 bg-surface rounded-2xl border border-surface-dark">
            <BsFillLightbulbFill className="w-14 h-14 text-yellow-400" />

            <div className="mx-auto text-center md:w-3/5">
              <h3 className="text-2xl font-semibold text-neutral-700">
                Your Needs Come First
              </h3>
              <p className="text-lg mt-2 text-neutral-600">
                The architecture of your organization dictates our approach, not
                the other way around.
              </p>
            </div>
          </div>

          <div className="grid  sm:grid-cols-2  gap-3">
            {supportCards.map((card, index) => (
              <div
                className="bg-surface-light space-y-6 rounded-2xl p-8 md:min-h-[18rem] min-h-auto border border-surface-dark "
                key={index}
              >
                <div className="flex items-center justify-center h-14 w-14 rounded-md border border-surface-dark bg-white">
                  <card.icon className="p-1 h-12 w-12 text-neutral-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-neutral-700">
                    {card?.title}
                  </h3>
                  <p className="mt-2 text-neutral-600">{card?.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-screen-lg mx-auto text-center">
          <CtaOne
            heading2="Brand Identity Customization"
            paragraph="Tailor BoloSign to align with your unique brand identity, ensuring your client feel confident they are interacting directly with your organization."
          />
        </div>

        <div className="mt-2 grid gap-8 sm:mx-auto sm:w-2/3 xl:w-full xl:grid-cols-3">
          {customization.map((custom, index) => (
            <div
              key={index}
              className="rounded-3xl border border-surface-dark  bg-surface p-12 "
            >
              <div className="space-y-8 text-center">
                {custom.icon}

                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold">{custom.title}</h3>
                  <p className="">{custom.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="md:w-2/3">
          <CtaOne
            heading2="Start leveraging the unique features of BoloSign Enterprise today"
            paragraph="Access functionalities that set us apart from the competition. We offer a range of unique features tailored to meet your needs."
          />
        </div>

        <div className="mt-4 xl:mt-8 grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {uniqueFeatures.map((feature, index) => (
            <Link
              key={index}
              className={`block group w-[min(90vw,400px)] mx-auto md:w-auto md:mx-0 border  rounded-2xl transition-all duration-500 ease-in-out border-surface-dark bg-surface hover:bg-surface-light`}
              href={feature.link}
            >
              <div className="h-full flex flex-col space-y-6 p-8">
                <div>
                  <div className="bg-white text-black rounded-full w-20 h-20 flex items-center justify-center border border-surface-dark">
                    {feature.icon}
                  </div>
                </div>

                <div className="h-full flex flex-col justify-between space-y-6">
                  <div className="space-y-2">
                    <p className="text-xl font-semibold text-neutral-700 transition group-hover:text-secondary-focus">
                      {feature.title}
                    </p>
                    <p className="text-neutral-600">{feature.description}</p>
                  </div>

                  <div>
                    <CLMButtonPrimaryArrow buttonText={"Learn more"} />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* <section>
        <div className="flex items-center bg-surface-light rounded-3xl py-20 px-4 ">
          <div className="mx-auto text-center">
            <h2 className="text-5xl font-semibold max-w-xl mx-auto">
              Get started with BoloSign Enterprise
            </h2>

            <div className="flex w-full justify-center mt-10">
              <CalComModalTrigger>
                <CLMButtonPrimary1
                  buttonText={"Talk to Sales"}
                  icon={<HiArrowLongRight />}
                />
              </CalComModalTrigger>
            </div>
          </div>
        </div>
      </section> */}
    </div>
  );
};

export default Enterprise;
